import React, { Suspense, useEffect, useState } from "react";

import { ProductTileModel } from "@api/mappers/products.mapper";

import { arrayHasContent } from "@utils/array";
import { ProductSliderSources } from "@utils/enums";
import { isDefined } from "@utils/helpers";
import { useSearchClient } from "@utils/hooks/algolia/useSearchClient.hook";
import { getBreakPointLayoutSettings } from "@utils/swiper";

import { searchProducts } from "@services/algolia/search/algoliaSearch.service";
import { ParamsAlgolia } from "@services/algolia/types/paramsAlgolia.types";

import { useGranite } from "@redux/modules/value/translations/translations.provider";

import { ProductSlider } from "@components/Product/ProductSlider/productSlider.component";
import { ProductSliderSkeletonComponent } from "@components/Product/ProductSlider/skeleton/productSliderSkeleton.component";

type Props = {
    title: string;
    showButton: boolean;
    showCategoryLink: boolean;
    useInsidePadding: boolean;
    amountOfFeatures: number;
    breakpointslayoutsettings: any;
    paramsAlgolia: ParamsAlgolia;
    ssrLayoutClasses?: string;
    lazyLoad: boolean;
};

const AlgoliaProductSlider = ({
    title,
    showButton,
    showCategoryLink,
    amountOfFeatures,
    useInsidePadding,
    breakpointslayoutsettings,
    paramsAlgolia,
    ssrLayoutClasses,
    lazyLoad,
}: Props) => {
    const { t } = useGranite();
    const [products, setProducts] = useState<ProductTileModel[]>([]);
    const [loading, setLoading] = useState(true);

    const searchClient = useSearchClient();

    useEffect(() => {
        if (!isDefined(paramsAlgolia) || !isDefined(searchClient)) return;

        searchProducts(searchClient)(paramsAlgolia)
            .then(products => {
                setProducts(products);
                setLoading(false);
            })
            .catch((error: any) => {
                setLoading(false);
                console.warn(
                    "Algolia ProductLister: unable to lookup products",
                    error,
                );
            });
    }, [paramsAlgolia, searchClient]);

    if (loading) {
        return (
            <ProductSliderSkeletonComponent
                amountOfFeatures={amountOfFeatures}
            />
        );
    } else if (!arrayHasContent(products)) {
        return null;
    }

    return (
        <Suspense fallback={<p>{t("common.loading")}</p>}>
            <ProductSlider
                lazyLoad={lazyLoad}
                title={title}
                products={products}
                productsource={ProductSliderSources.algolia}
                showButton={showButton}
                amountOfFeatures={amountOfFeatures}
                showCategoryLink={showCategoryLink}
                useInsidePadding={useInsidePadding}
                breakpointslayoutsettings={getBreakPointLayoutSettings(
                    breakpointslayoutsettings,
                )}
                ssrLayoutClasses={ssrLayoutClasses}
                analyticsContext={{
                    list: "product-slider-search-node",
                    listTitle: title,
                }}
            />
        </Suspense>
    );
};
export { AlgoliaProductSlider };
